import React, { useState, useEffect } from 'react';
import { Send, FileText, RefreshCw, ChevronDown, ChevronUp, Upload, X, Bot } from 'lucide-react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import './941572d17bc0d98b.css';
import './App.css';  // Temporary: Keep custom component styles while refactoring to Tailwind
import './styles.css';  // Customer's augmented styles (including AI suggestion buttons)
import { BACKEND_ADDRESS, exampleTexts, BASE_STARTING_POINT } from './utils/constants';
import { parseTimeframe, combineTimeframe, isValidYearMonth } from './utils/dateHelpers';
import { getScoreStyle, getScoreLabel } from './utils/scoringHelpers';
import * as api from './services/api';
import LinkedInHelpPopup from './components/shared/LinkedInHelpPopup';
import LinkedInLogo from './components/shared/LinkedInLogo';
import GuideContent from './GuideContent';
import guideData from './guide-questions.json';
import OnboardingHelpButton from './components/onboarding/OnboardingHelpButton';
import OnboardingHints from './components/onboarding/OnboardingHints';
// Optional: For future use when refactoring loading states
// import { LoadingPlaceholder, ErrorPlaceholder, UnsavedChangesPlaceholder } from './components/shared/LoadingStates';
// import { useTaxonomyData } from './hooks/useTaxonomyData';

function CVBuilder() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Guard against an invalid session id in the URL. This happens, for example, when
  // the app is served behind a reverse proxy that leaves a duplicated path prefix
  // (e.g. /cv-builder/cv-builder): the router then reads the literal "cv-builder" as
  // the user_id and every backend call 404s. Valid ids are 6 lowercase alphanumerics
  // (matching the backend's is_valid_user_id), the "demo" id, or the "new-user" route.
  // Anything else: drop it and start a clean session instead of firing 404s.
  useEffect(() => {
    const isValidSessionId = (id) =>
      id === 'new-user' || id.includes('demo') || /^[a-z0-9]{6}$/.test(id);
    if (userId && !isValidSessionId(userId)) {
      console.warn(`⚠️ Ignoring invalid session id in URL: "${userId}" — starting a fresh session`);
      navigate('/', { replace: true });
    }
  }, [userId, navigate]);

  // ALL HOOKS MUST BE DECLARED FIRST - NO CONDITIONS BEFORE HOOKS
  const [currentQuestion, setCurrentQuestion] = useState("Custom request");
  const [userInput, setUserInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [cvScore, setCvScore] = useState(1);
  const [cvComment, setCvComment] = useState("CV is empty. Start by adding your basic information.");
  const [showDetails, setShowDetails] = useState(false);
  
  // New state for the modal
  const [showCreateNewModal, setShowCreateNewModal] = useState(false);
  const [newCvText, setNewCvText] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isCreatingCV, setIsCreatingCV] = useState(false);
  
  // States for animated placeholder
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  
  // State for LinkedIn help popup
  const [showLinkedInHelp, setShowLinkedInHelp] = useState(false);
  const [linkedInIconRef, setLinkedInIconRef] = useState(null);
  
  // State for left section tabs
  const [activeTab, setActiveTab] = useState('guide'); // 'guide', 'pdf', or 'form'
  
  // State for anquet form data
  const [anquetData, setAnquetData] = useState(null);
  const [anquetLoading, setAnquetLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [anquetError, setAnquetError] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  // Removed lastFetchedAnquetData since autosaving is disabled
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track immediate changes
  const [isPdfUpdating, setIsPdfUpdating] = useState(false); // Track when PDF is being updated
  
  // Taxonomy data state
  const [taxonomyData, setTaxonomyData] = useState({
    languages: [],
    proficiencyLevels: [],
    educationLevels: [],
    socialPlatforms: [],
    jobCategories: [],
    technicalSkills: [],
    softSkills: [],
    cities: [],
    countries: []
  });
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // State for Guide tab navigation and CV upload/prefilling
  const [guideCurrentFrame, setGuideCurrentFrame] = useState(1);
  const [isPrefillingFromCV, setIsPrefillingFromCV] = useState(false);

  // State for AI improvement functionality
  const [isImprovingField, setIsImprovingField] = useState(false);
  const [improvementTarget, setImprovementTarget] = useState(null); // {type: 'about' | 'experience', index?: number}
  const [hoveredField, setHoveredField] = useState(null); // Track which field is being hovered for AI button

  const [suggestedQuestions, setSuggestedQuestions] = useState([
    "Custom request",
    "What is your full name?",
    "What is your phone number or email?",
    "What is your current or most recent job?",
    "What qualifications or skills do you have?",
    "Where are you located?",
  ]);

  // ALL useEffect HOOKS MUST BE DECLARED AFTER ALL useState HOOKS
  // Typing animation effect
  useEffect(() => {
    // Show animated placeholder when create-new modal is open and textarea empty
    if (!showCreateNewModal || newCvText.length > 0) return;

    let timeoutId;
    let typingInterval;

    const startTyping = () => {
      setIsTyping(true);
      setDisplayedText("");
      
      const currentText = exampleTexts[currentExampleIndex];
      let charIndex = 0;

      typingInterval = setInterval(() => {
        if (charIndex < currentText.length) {
          setDisplayedText(currentText.slice(0, charIndex + 1));
          charIndex++;
        } else {
          clearInterval(typingInterval);
          setIsTyping(false);
          
          // Wait 3 seconds before starting next example
          timeoutId = setTimeout(() => {
            setCurrentExampleIndex((prev) => (prev + 1) % exampleTexts.length);
          }, 3000);
        }
      }, 20); // Typing speed - 30ms per character
    };

    startTyping();

    return () => {
      clearInterval(typingInterval);
      clearTimeout(timeoutId);
    };
  }, [currentExampleIndex, showCreateNewModal, newCvText]);

  // Auto-load PDF on component mount and cleanup on unmount
  useEffect(() => {
    const init = async () => {
      // If no user session, configure initial view based on BASE_STARTING_POINT
      if (!userId) {
        console.log("👋 No session detected. Configuring initial view based on BASE_STARTING_POINT:", BASE_STARTING_POINT);
        setPdfLoading(false);

        // Configure initial view based on BASE_STARTING_POINT setting
        switch (BASE_STARTING_POINT) {
          case 'GUIDE_TAB':
            // Default: Guide tab
            setActiveTab('guide');
            console.log("📋 Starting with Guide tab");
            break;
          case 'PDF':
            // Fallback to GUIDE_TAB since no PDF exists for new users
            setActiveTab('guide');
            console.log("⚠️ PDF option not available for new users. Falling back to Guide tab");
            break;
          default:
            // Unknown option, fallback to GUIDE_TAB
            setActiveTab('guide');
            console.log("⚠️ Unknown BASE_STARTING_POINT:", BASE_STARTING_POINT, "- Falling back to Guide tab");
        }

        // Initialize empty anquetData for form usage
        setAnquetData({
          personal: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            country: '',
            city: '',
            job_category: '',
            social_links: []
          },
          about: '',
          experience: [],
          education: [],
          soft_skills: { skills: [] },
          technical_skills: { skills: [] },
          languages: []
        });
        await loadTaxonomyData();
        return;
      }

      try {
        console.log("🚀 Loading CV data for user:", userId);
        console.log("🔗 Backend address:", BACKEND_ADDRESS);

        // Fetch existing anquet data (user was already initialized in /api/new-user)
        await fetchAnquetData(false, userId);

        // Try to load PDF if it exists
        try {
          const ts = new Date().getTime();
          const pdfResponse = await fetch(`${BACKEND_ADDRESS}/api/${userId}/pdf/current?t=${ts}`);
          if (pdfResponse.ok) {
            const blob = await pdfResponse.blob();
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            setIsPdfUpdating(false);
            console.log("📄 PDF loaded successfully");
            // Check if we should stay on Guide tab (e.g., after "Generate CV with AI" button)
            if (location.state?.stayOnGuide) {
              setActiveTab('guide');
              if (location.state.guideFrame) {
                setGuideCurrentFrame(location.state.guideFrame);
              }
              console.log("📋 Staying on Guide tab (from navigation state)");
              // Clear the navigation state to prevent it from persisting on refresh
              window.history.replaceState({}, document.title);
            } else if (location.state?.switchToPdf) {
              // Explicit request to switch to PDF tab (e.g., from Generate CV button)
              setActiveTab('pdf');
              console.log("📄 Switching to PDF tab (from navigation state)");
              // Clear the navigation state to prevent it from persisting on refresh
              window.history.replaceState({}, document.title);
            } else if (areAllRequiredFieldsFilled()) {
              // Default to PDF tab only if all required fields are filled
              setActiveTab('pdf');
            } else {
              // Otherwise start with Guide tab so user can complete missing fields
              setActiveTab('guide');
              console.log("📋 Required fields incomplete, starting with Guide tab");
            }
          } else {
            // No PDF exists yet - this is normal for new users
            setIsPdfUpdating(false);
            // If switchToPdf was requested, honor it (PDF will show loading/empty state)
            if (location.state?.switchToPdf) {
              setActiveTab('pdf');
              console.log("📄 Switching to PDF tab (from navigation state), PDF not ready yet");
              window.history.replaceState({}, document.title);
            } else {
              setActiveTab('guide');
              console.log("📋 No PDF found, starting with Guide tab");
            }
          }
        } catch (pdfError) {
          console.log("📋 No PDF available yet, starting with Guide tab");
          setIsPdfUpdating(false);
          // If switchToPdf was requested, honor it even if PDF fetch failed
          if (location.state?.switchToPdf) {
            setActiveTab('pdf');
            console.log("📄 Switching to PDF tab (from navigation state), PDF fetch failed");
            window.history.replaceState({}, document.title);
          } else {
            setActiveTab('guide');
          }
        }
      } catch (err) {
        console.error("❌ Error loading CV:", err);
        setPdfError(err.message);
        // Stop updating spinner on error to avoid perpetual spinner
        setIsPdfUpdating(false);
      } finally {
        setPdfLoading(false);
        await loadTaxonomyData();
      }
    };

    init();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Handle tab switching logic
  useEffect(() => {
    if ((activeTab === 'form' || activeTab === 'guide') && !anquetData && !anquetLoading) {
      // First time opening form or guide tab - fetch data
      fetchAnquetData();
    } else if (activeTab === 'pdf' && !hasUnsavedChanges && !isPdfUpdating) {
      // Refresh PDF when switching to PDF tab (only if no unsaved changes and not already updating)
      console.log("📄 Refreshing PDF when switching to PDF tab");
      setIsPdfUpdating(true);
      refreshPDF();
    } else if (activeTab === 'pdf' && isPdfUpdating) {
      // If already updating (e.g., from Preview button click), just refresh
      console.log("📄 PDF already updating, refreshing now");
      refreshPDF();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Note: We no longer auto-switch away from PDF when there are unsaved changes
  // Instead, clicking Preview will auto-save changes before switching

  // If someone accessed /new-user, redirect to backend
  if (userId === 'new-user') {
    window.location.href = `${BACKEND_ADDRESS}/new-user`;
    return (
      <div className="app">
        <div className="pdf-section">
          <div className="pdf-header">
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button className="tab-button active">
              Preview
            </button>
            <button className="tab-button">
              Form
            </button>
          </div>

          <div className="pdf-container">
            <div className="pdf-placeholder">
              <RefreshCw size={48} className="spinning" />
              <p>Redirecting to new session...</p>
            </div>
          </div>
        </div>
        <div className="controls-section">
          <div className="current-question">
            <h4>Setting up your workspace...</h4>
            <p>Creating a new CV session</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadedFile(e.dataTransfer.files[0]);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };

  // Handle create new CV submission
  const handleCreateNewCV = async () => {
    if (!newCvText.trim() && !uploadedFile) {
      alert("Please provide either text information or upload a document");
      return;
    }

    setIsCreatingCV(true);
    // If we're on PDF tab, hide outdated PDF since we're about to create a new one
    if (activeTab === 'pdf') {
      setIsPdfUpdating(true);
    }
    
    try {
      const formData = new FormData();
      
      if (newCvText.trim()) {
        formData.append('text_input', newCvText);
      }
      
      if (uploadedFile) {
        formData.append('document', uploadedFile);
      }

      const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/initialize-cv`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      console.log("🎉 Initial CV creation response:", data);

      if (response.ok) {
        // Close modal and reset form
        setShowCreateNewModal(false);
        setNewCvText("");
        setUploadedFile(null);
        
        // Update the UI with the new CV data
        if (data.suggested_questions && data.suggested_questions.length > 0) {
          setSuggestedQuestions(data.suggested_questions);
          setCurrentQuestion(data.suggested_questions[0]);
        }
        
        if (data.cv_score !== undefined) {
          setCvScore(data.cv_score);
        }
        
        if (data.cv_comment) {
          setCvComment(data.cv_comment);
        }
        
        // Refresh PDF
        if (data.pdf_updated) {
          await refreshPDF();
          
          // Refresh form data to reflect the newly created CV
          console.log("🔄 Refreshing form data after creating new CV");
          await fetchAnquetData(true); // Force refresh
        }
      } else {
        alert(data.message || "Failed to create new CV");
      }
    } catch (error) {
      console.error("❌ Error creating new CV:", error);
      alert("Error creating new CV. Please try again.");
      // If there was an error, clear the updating state
      setIsPdfUpdating(false);
    } finally {
      setIsCreatingCV(false);
    }
  };

  const refreshPDF = async () => {
    if (!userId || userId === 'new-user') return;
    try {
      const blob = await api.fetchPDF(userId);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setIsPdfUpdating(false);
    } catch (err) {
      console.error("❌ Error refreshing PDF:", err);
      setIsPdfUpdating(false);
    }
  };

  const fetchAnquetData = async (forceRefresh = false, explicitUserId = null) => {
    const targetUserId = explicitUserId || userId;
    if (!targetUserId || targetUserId === 'new-user') return;
    setAnquetLoading(true);
    try {
      const data = await api.fetchAnquetData(targetUserId);
      if (data.status === 'success' && data.anquet) {
        const cleanedAnquet = { ...data.anquet };

        // Migrate deprecated 'name' field to first_name/last_name if needed
        if (cleanedAnquet.personal && 'name' in cleanedAnquet.personal) {
          // If first_name/last_name don't exist, split the name
          if (!cleanedAnquet.personal.first_name && !cleanedAnquet.personal.last_name) {
            const fullName = cleanedAnquet.personal.name || '';
            const nameParts = fullName.trim().split(/\s+/);
            if (nameParts.length > 1) {
              cleanedAnquet.personal.first_name = nameParts.slice(0, -1).join(' ');
              cleanedAnquet.personal.last_name = nameParts[nameParts.length - 1];
            } else {
              cleanedAnquet.personal.first_name = nameParts[0] || '';
              cleanedAnquet.personal.last_name = '';
            }
          }
          // Remove the deprecated 'name' field
          delete cleanedAnquet.personal.name;
        }

        // Migrate deprecated 'job_category' (single) to 'job_categories' (array)
        if (cleanedAnquet.personal && 'job_category' in cleanedAnquet.personal) {
          // If job_categories doesn't exist or is empty, migrate from job_category
          if (!cleanedAnquet.personal.job_categories || cleanedAnquet.personal.job_categories.length === 0) {
            const deprecatedCategory = cleanedAnquet.personal.job_category;
            if (deprecatedCategory) {
              cleanedAnquet.personal.job_categories = [deprecatedCategory];
            } else {
              cleanedAnquet.personal.job_categories = [];
            }
          }
          // Remove the deprecated 'job_category' field
          delete cleanedAnquet.personal.job_category;
        }

        // Ensure job_categories is initialized as an array
        if (cleanedAnquet.personal && !cleanedAnquet.personal.job_categories) {
          cleanedAnquet.personal.job_categories = [];
        }

        if (!anquetData || forceRefresh) {
          setAnquetData(cleanedAnquet);
        }

        // Update assessment data (questions, score, comment) if present in response
        if (data.suggested_questions && data.suggested_questions.length > 0) {
          setSuggestedQuestions(data.suggested_questions);
          setCurrentQuestion(data.suggested_questions[0]);
          console.log("✅ Updated suggested questions from /anquet:", data.suggested_questions.slice(0, 3));
        }
        if (data.cv_score !== undefined) {
          setCvScore(data.cv_score);
          console.log("✅ Updated CV score from /anquet:", data.cv_score);
        }
        if (data.cv_comment) {
          setCvComment(data.cv_comment);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching anquet data:", err);
      setAnquetError(err.message);
    } finally {
      setAnquetLoading(false);
    }
  };

  const saveAnquetData = async (updatedData, refreshPdfNow = false) => {
    if (!userId || userId === 'new-user') return { success: false, message: 'No valid user session' };

    setFormSaving(true);
    try {
      // Clean the data before sending
      const cleanedData = { ...updatedData };

      // Remove deprecated 'name' field if it exists (only use first_name/last_name now)
      if (cleanedData.personal && 'name' in cleanedData.personal) {
        const { name, ...restPersonal } = cleanedData.personal;
        cleanedData.personal = restPersonal;
      }

      // Filter out empty language entries (both language and proficiency must be non-empty)
      if (cleanedData.languages && Array.isArray(cleanedData.languages)) {
        cleanedData.languages = cleanedData.languages.filter(lang =>
          lang && lang.language && lang.language.trim() !== '' &&
          lang.proficiency && lang.proficiency.trim() !== ''
        );
      }

      // Normalize is_current field in experience items (must be boolean, not empty string)
      if (cleanedData.experience && Array.isArray(cleanedData.experience)) {
        cleanedData.experience = cleanedData.experience.map(exp => ({
          ...exp,
          is_current: exp.is_current === true || exp.is_current === 'true'
        }));
      }

      // Normalize is_current field in education items (must be boolean, not empty string)
      if (cleanedData.education && Array.isArray(cleanedData.education)) {
        cleanedData.education = cleanedData.education.map(edu => ({
          ...edu,
          is_current: edu.is_current === true || edu.is_current === 'true'
        }));
      }

      // Note: We no longer filter out empty social links during save.
      // Empty social links are allowed so users can add and fill them in.
      // The backend filters them out during PDF generation (app.py:393-400).

      console.log("💾 Saving anquet data for user:", userId, cleanedData);
      const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/update-anquet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedData)
      });

      if (!response.ok) {
        throw new Error(`Failed to save anquet data: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ Anquet data saved:", data);
      
      if (data.status === 'success') {
        // Update local state
        setAnquetData(data.anquet);

        // Update questions from backend
        if (data.suggested_questions && data.suggested_questions.length > 0) {
          setSuggestedQuestions(data.suggested_questions);
          setCurrentQuestion(data.suggested_questions[0]);
        }

        // Update score and comment if provided by backend
        if (data.cv_score !== undefined) {
          setCvScore(data.cv_score);
        }

        if (data.cv_comment) {
          setCvComment(data.cv_comment);
        }

        // Clear unsaved changes flag after successful save
        setHasUnsavedChanges(false);

        // Only refresh PDF if we're on PDF tab
        // If refreshPdfNow is true, we'll let the tab switch trigger the refresh
        if (data.pdf_updated && activeTab === 'pdf' && !refreshPdfNow) {
          await refreshPDF();
        } else if (refreshPdfNow) {
          // Set PDF updating state for the upcoming tab switch
          setIsPdfUpdating(true);
        } else {
          // If we're not refreshing PDF now, clear the updating state
          setIsPdfUpdating(false);
        }
        
        return { success: true, message: data.message };
      } else {
        throw new Error(data.message || 'Failed to save anquet data');
      }
      
    } catch (err) {
      console.error("❌ Error saving anquet data:", err);
      return { success: false, message: err.message };
    } finally {
      setFormSaving(false);
    }
  };

  const loadTaxonomyData = async () => {
    setTaxonomyLoading(true);
    try {
      const data = await api.loadTaxonomyData();
      setTaxonomyData(data);
    } catch (err) {
      console.error("❌ Error loading taxonomy data:", err);
      // Fallback is handled in the hook
    } finally {
      setTaxonomyLoading(false);
    }
  };

  // AI Improvement functionality
  // eslint-disable-next-line no-unused-vars
  const improveFieldWithAI = async (fieldType, currentText, experienceIndex = null) => {
    if (!userId || userId === 'new-user') return;

    setIsImprovingField(true);
    setImprovementTarget({ type: fieldType, index: experienceIndex });

    try {
      console.log(`🤖 Improving ${fieldType} field with AI...`);

      const data = await api.improveField(userId, fieldType, currentText, experienceIndex);
      console.log("✅ AI improvement response:", data);

      if (data.status === 'success') {
        // Update the field with improved text
        let updatedData;
        if (fieldType === 'about') {
          updateAboutField(data.improved_text);
          updatedData = {
            ...anquetData,
            about: data.improved_text
          };
        } else if (fieldType === 'experience_description' && experienceIndex !== null) {
          const improvedDescription = data.improved_text.split('\n').filter(line => line.trim());
          updateExperienceField(experienceIndex, 'description', improvedDescription);
          updatedData = {
            ...anquetData,
            experience: (anquetData.experience || []).map((exp, i) =>
              i === experienceIndex ? { ...exp, description: improvedDescription } : exp
            )
          };
        }
        
        // Auto-save the improved data
        if (updatedData) {
          console.log("💾 Auto-saving improved text...");
          await saveAnquetData(updatedData, activeTab === 'pdf');
        }
        
        // Show success feedback
        console.log("🎉 Field improved and saved successfully!");
        
      } else {
        console.error("❌ AI improvement failed:", data.message);
        alert(`Failed to improve text: ${data.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error("❌ Error improving field:", error);
      alert("Error improving text. Please try again.");
    } finally {
      setIsImprovingField(false);
      setImprovementTarget(null);
    }
  };

  // handleClearForm removed (unused)

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    setIsProcessing(true);

    // Auto-create userId if it doesn't exist
    let currentUserId = userId;
    if (!currentUserId) {
      try {
        console.log("🆕 Creating new user session...");
        const newUserData = await api.createNewUser();
        currentUserId = newUserData.user_id;
        // Navigate to the new user's URL (relative to basename)
        navigate(`/${currentUserId}`);
        console.log(`✅ New user created: ${currentUserId}`);
      } catch (error) {
        console.error("❌ Error creating new user:", error);
        alert("Failed to create user session. Please try again.");
        setIsProcessing(false);
        return;
      }
    }

    // Save any unsaved changes before sending to AI
    // This ensures local changes (like deleted work experience) are persisted
    // before the backend processes the AI request
    if (hasUnsavedChanges && anquetData && currentUserId) {
      console.log("💾 Auto-saving changes before AI submission");
      try {
        const saveResult = await saveAnquetData(anquetData, false); // Don't refresh PDF yet
        if (!saveResult.success) {
          console.error("❌ Failed to save changes before AI submission:", saveResult.message);
          alert("Failed to save your changes. Please try again.");
          setIsProcessing(false);
          return;
        }
        console.log("✅ Changes saved successfully before AI submission");
      } catch (saveError) {
        console.error("❌ Error saving changes before AI submission:", saveError);
        alert("Failed to save your changes. Please try again.");
        setIsProcessing(false);
        return;
      }
    }

    // Determine if we should generate PDF
    // Only generate PDF if on Preview tab AND all required fields are filled
    const shouldGeneratePdf = activeTab === 'pdf' && areAllRequiredFieldsFilled();

    // If we're on PDF tab, hide outdated PDF since we're about to update it
    if (activeTab === 'pdf') {
      setIsPdfUpdating(true);
    }
    console.log("📤 Sending to backend:", userInput, `(generate_pdf: ${shouldGeneratePdf})`);

    try {
      const data = await api.processInput(currentUserId, userInput, currentQuestion, shouldGeneratePdf);
      console.log("📥 Backend response:", data);
      
      // Update questions from backend
      if (data.suggested_questions && data.suggested_questions.length > 0) {
        setSuggestedQuestions(data.suggested_questions);
        setCurrentQuestion(data.suggested_questions[0]);
      } else if (data.next_question) {
        setCurrentQuestion(data.next_question);
      }
      
      // Update score and comment if provided by backend
      if (data.cv_score !== undefined) {
        setCvScore(data.cv_score);
      }
      
      if (data.cv_comment) {
        setCvComment(data.cv_comment);
      }
      
      // Always refresh form data after AI processes input
      // This ensures AI updates are reflected in the questionnaire
      console.log("🔄 Refreshing form data after AI update");
      await fetchAnquetData(true, currentUserId); // Force refresh with current user ID

      // Only refresh PDF if it was actually generated
      if (data.pdf_updated) {
        await refreshPDF();
      }
      
    } catch (error) {
      console.error("❌ Error processing input:", error);
      // If there was an error, clear the updating state
      setIsPdfUpdating(false);
    } finally {
      setIsProcessing(false);
      setUserInput("");
    }
  };

  const handleExport = async () => {
    try {
      const blob = await api.exportCV(userId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cv-export.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ Export error:", error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGenerateCV = async () => {
    // If there are unsaved changes, save them first
    if (hasUnsavedChanges && anquetData) {
      console.log("💾 Saving changes before generating CV");
      const saveResult = await saveAnquetData(anquetData, true); // Force PDF refresh
      
      if (!saveResult.success) {
        alert("Failed to save changes before generating CV. Please try again.");
        return;
      }
    }
    
    // Then proceed with export
    await handleExport();
  };

  const handleExportJSON = async () => {
    console.log("📤 Requesting JSON export...");
    try {
      const response = await fetch(`${BACKEND_ADDRESS}/api/${userId}/export-json`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) {
        throw new Error('JSON export failed');
      }
      const blob = await api.exportJSON(userId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cv-export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log("💾 JSON download initiated");
    } catch (error) {
      console.error("❌ JSON Export error:", error);
    }
  };

  // Handle switching to Preview tab with auto-save or CV generation
  const handlePreviewTabClick = async () => {
    // If no userId exists yet, initialize the CV first
    if (!userId) {
      console.log("🚀 No userId - initializing CV from Preview button click");
      await handleInitializeCV();
      return;
    }

    // If there are unsaved changes, save them first
    if (hasUnsavedChanges && anquetData) {
      console.log("💾 Auto-saving changes before switching to Preview");
      const saveResult = await saveAnquetData(anquetData, true); // Force PDF refresh

      if (!saveResult.success) {
        alert("Failed to save changes. Please try saving manually before viewing the preview.");
        return;
      }
    }

    // Switch to preview tab
    setActiveTab('pdf');
  };

  // Update functions for form fields
  // eslint-disable-next-line no-unused-vars
  const updatePersonalField = (field, value) => {
    const updatedData = {
      ...anquetData,
      personal: {
        ...anquetData.personal,
        [field]: value
      }
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  const updateAboutField = (value) => {
    const updatedData = {
      ...anquetData,
      about: value
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  const updateExperienceField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      experience: (anquetData.experience || []).map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp
      )
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addExperience = () => {
    // Add an empty experience object - structure will be determined by existing data or defaults
    let newExp = (anquetData.experience && anquetData.experience.length > 0)
      ? Object.keys(anquetData.experience[0]).reduce((acc, key) => {
          // For description fields, initialize as empty array
          const value = (key === 'description' || key === 'items') ? [] : '';
          return { ...acc, [key]: value };
        }, {})
      : { company: '', role: '', timeframe: '', start_date: '', end_date: '', country: '', city: '', description: [] };
    // Ensure new experience has required keys
    if (newExp.country === undefined) newExp.country = '';
    if (newExp.city === undefined) newExp.city = '';
    if (newExp.start_date === undefined) newExp.start_date = '';
    if (newExp.end_date === undefined) newExp.end_date = '';

    const updatedData = {
      ...anquetData,
      experience: [...(anquetData.experience || []), newExp]
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeExperience = (index) => {
    const updatedData = {
      ...anquetData,
      experience: (anquetData.experience || []).filter((_, i) => i !== index)
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const updateEducationField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      education: (anquetData.education || []).map((edu, i) =>
        i === index ? { ...edu, [field]: value } : edu
      )
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addEducation = () => {
    let newEdu = (anquetData.education && anquetData.education.length > 0)
      ? Object.keys(anquetData.education[0]).reduce((acc, key) => ({ ...acc, [key]: '' }), {})
      : { institution: '', level: '', timeframe: '', start_date: '', end_date: '', country: '', city: '', items: [] };
    if (newEdu.country === undefined) newEdu.country = '';
    if (newEdu.city === undefined) newEdu.city = '';
    if (newEdu.start_date === undefined) newEdu.start_date = '';
    if (newEdu.end_date === undefined) newEdu.end_date = '';

    const updatedData = {
      ...anquetData,
      education: [...(anquetData.education || []), newEdu]
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeEducation = (index) => {
    const updatedData = {
      ...anquetData,
      education: (anquetData.education || []).filter((_, i) => i !== index)
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const updateTechnicalSkills = (selectedSkills) => {
    const updatedData = {
      ...anquetData,
      technical_skills: { skills: selectedSkills }
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const updateSoftSkills = (selectedSkills) => {
    const updatedData = {
      ...anquetData,
      soft_skills: { skills: selectedSkills }
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const updateJobCategories = (selectedCategories) => {
    const updatedData = {
      ...anquetData,
      personal: {
        ...anquetData.personal,
        job_categories: selectedCategories
      }
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // Languages handlers
  // eslint-disable-next-line no-unused-vars
  const updateLanguageField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      languages: (anquetData.languages || []).map((lang, i) =>
        i === index ? { ...lang, [field]: value } : lang
      )
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addLanguage = () => {
    const newLanguage = { language: '', proficiency: '' };

    const updatedData = {
      ...anquetData,
      languages: [...(anquetData.languages || []), newLanguage]
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeLanguage = (index) => {
    const updatedData = {
      ...anquetData,
      languages: (anquetData.languages || []).filter((_, i) => i !== index)
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // Qualifications handlers
  // eslint-disable-next-line no-unused-vars
  const updateQualificationField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      qualifications: (anquetData.qualifications || []).map((qual, i) =>
        i === index ? { ...qual, [field]: value } : qual
      )
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addQualification = () => {
    const newQualification = { qualification: '', issue_date: '' };

    const updatedData = {
      ...anquetData,
      qualifications: [...(anquetData.qualifications || []), newQualification]
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeQualification = (index) => {
    const updatedData = {
      ...anquetData,
      qualifications: (anquetData.qualifications || []).filter((_, i) => i !== index)
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // Awards handlers
  // eslint-disable-next-line no-unused-vars
  const updateAwardField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      awards: (anquetData.awards || []).map((award, i) =>
        i === index ? { ...award, [field]: value } : award
      )
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addAward = () => {
    const newAward = { award: '', issue_date: '' };

    const updatedData = {
      ...anquetData,
      awards: [...(anquetData.awards || []), newAward]
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeAward = (index) => {
    const updatedData = {
      ...anquetData,
      awards: (anquetData.awards || []).filter((_, i) => i !== index)
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const updateSocialLinkField = (index, field, value) => {
    const updatedData = {
      ...anquetData,
      personal: {
        ...anquetData.personal,
        social_links: anquetData.personal.social_links.map((link, i) => 
          i === index ? { ...link, [field]: value } : link
        )
      }
    };
    setAnquetData(updatedData);
    setHasUnsavedChanges(true);
  };

  // eslint-disable-next-line no-unused-vars
  const addSocialLink = () => {
    const newLink = { platform: '', url: '' };
    const updatedData = {
      ...anquetData,
      personal: {
        ...anquetData.personal,
        social_links: [...(anquetData.personal.social_links || []), newLink]
      }
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  // eslint-disable-next-line no-unused-vars
  const removeSocialLink = (index) => {
    const updatedData = {
      ...anquetData,
      personal: {
        ...anquetData.personal,
        social_links: anquetData.personal.social_links.filter((_, i) => i !== index)
      }
    };
    setAnquetData(updatedData);
    saveAnquetData(updatedData, false); // Don't auto-refresh PDF
  };

  const handleQuestionClick = (question) => {
    console.log("🎯 Question selected:", question);
    setCurrentQuestion(question);
  };

  // Guide helper functions
  const getCurrentSection = () => {
    return guideData.sections[guideCurrentFrame - 1];
  };

  const getTotalFrames = () => {
    return guideData.sections.length;
  };

  const isCurrentSectionValid = () => {
    const currentSection = getCurrentSection();
    if (!currentSection || !anquetData) return false;

    // Special handling for experience list section
    if (currentSection.type === 'experience_list') {
      // If list is not required, allow skipping
      if (currentSection.required === false) {
        // If none added, section is valid
        if (!anquetData.experience || anquetData.experience.length === 0) return true;
      }
      // If list is required or has entries, at least one experience must have all required fields
      if (!anquetData.experience || anquetData.experience.length === 0) {
        return currentSection.required === false;
      }
      return anquetData.experience.every(exp => {
        return currentSection.fields.every(field => {
          if (!field.required) return true;
          if (field.name === 'timeframe') {
            const hasStartDate = exp.start_date && String(exp.start_date).trim();
            const hasEndDate = exp.end_date && String(exp.end_date).trim();
            const isCurrent = exp.is_current === true;
            return !!(hasStartDate && (hasEndDate || isCurrent));
          }
          const value = exp[field.name];
          return !!(value && String(value).trim());
        });
      });
    }

    // Special handling for education list section
    if (currentSection.type === 'education_list') {
      // If list is not required, allow skipping
      if (currentSection.required === false) {
        // If none added, section is valid
        if (!anquetData.education || anquetData.education.length === 0) return true;
      }
      // If list is required or has entries, at least one education must have all required fields
      if (!anquetData.education || anquetData.education.length === 0) {
        return currentSection.required === false;
      }
      return anquetData.education.every(edu => {
        return currentSection.fields.every(field => {
          if (!field.required) return true;
          if (field.name === 'timeframe') {
            const hasStartDate = edu.start_date && String(edu.start_date).trim();
            const hasEndDate = edu.end_date && String(edu.end_date).trim();
            const isCurrent = edu.is_current === true;
            return !!(hasStartDate && (hasEndDate || isCurrent));
          }
          const value = edu[field.name];
          return !!(value && String(value).trim());
        });
      });
    }

    // Special handling for multi_list_section (Skills section with groups)
    if (currentSection.type === 'multi_list_section') {
      return currentSection.groups.every(group => {
        // If group is not required, it's always valid
        if (group.required === false) return true;

        // For multi_select groups (soft_skills, technical_skills)
        if (group.type === 'multi_select') {
          const skills = anquetData?.[group.name]?.skills;
          return skills && skills.length > 0;
        }

        // For list groups (languages, qualifications, awards, social_links)
        if (group.type === 'list') {
          const getListData = () => {
            if (group.name === 'social_links') return anquetData?.personal?.social_links;
            return anquetData?.[group.name];
          };
          const listData = getListData();

          // If list is required, must have at least one complete entry
          if (!listData || listData.length === 0) return false;

          // At least one item must have all required fields filled
          return listData.some(item => {
            return group.fields.every(field => {
              if (!field.required) return true;
              const value = item[field.name];
              return value && String(value).trim();
            });
          });
        }

        return true;
      });
    }

    // Check if all required fields in current section are filled
    return currentSection.fields?.every(field => {
      if (!field.required) return true;
      const value = field.name === 'about' ? anquetData.about : anquetData.personal?.[field.name];
      return value && String(value).trim();
    }) ?? true;
  };

  // Helper function to check if a field value is filled
  const isFieldFilled = (value, fieldType) => {
    if (value === null || value === undefined) return false;

    // For string/text fields
    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    // For arrays (used in multi_select or when checking if list exists)
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // For objects
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  };

  // Helper function to validate required fields in a list item
  const validateListItem = (item, fieldDefinitions) => {
    if (!fieldDefinitions) return true;

    for (const fieldDef of fieldDefinitions) {
      if (fieldDef.required) {
        const value = item[fieldDef.name];
        if (!isFieldFilled(value, fieldDef.type)) {
          return false;
        }
      }
    }
    return true;
  };

  // Helper function to get field definitions from walkthrough-questions.json
  const getFieldDefinitions = (sectionName) => {
    if (!guideData?.sections) return null;

    for (const section of guideData.sections) {
      // Check if it's experience or education list section
      if (section.type === 'experience_list' && sectionName === 'experience') {
        return section.fields;
      }
      if (section.type === 'education_list' && sectionName === 'education') {
        return section.fields;
      }

      // Check if it's in the multi_list_section (Additional Information)
      if (section.type === 'multi_list_section' && section.groups) {
        for (const group of section.groups) {
          if (group.name === sectionName) {
            return group.fields || null;
          }
        }
      }
    }

    return null;
  };

  // Helper function to get incomplete field details for tooltip
  const getIncompleteFieldsMessage = () => {
    if (!anquetData) return 'Please fill in all required fields';

    const incompleteFields = [];

    // Check first_name and last_name
    const firstName = anquetData.personal?.first_name;
    const lastName = anquetData.personal?.last_name;

    if (!firstName || String(firstName).trim() === '') {
      incompleteFields.push('First Name');
    }
    if (!lastName || String(lastName).trim() === '') {
      incompleteFields.push('Last Name');
    }

    // Check required sections from walkthrough-questions.json
    if (guideData?.sections) {
      for (const section of guideData.sections) {
        // Check required list sections (experience_list, education_list)
        if (section.type === 'experience_list' && section.required === true) {
          if (!anquetData.experience || anquetData.experience.length === 0) {
            incompleteFields.push('Work Experience section is required');
          } else {
            // Check if at least one complete entry exists
            const hasCompleteEntry = anquetData.experience.some(exp => {
              return section.fields.every(field => {
                if (!field.required) return true;
                const value = exp[field.name];
                return value && String(value).trim();
              });
            });
            if (!hasCompleteEntry) {
              incompleteFields.push('Work Experience: At least one complete entry is required');
            }
          }
        }

        if (section.type === 'education_list' && section.required === true) {
          if (!anquetData.education || anquetData.education.length === 0) {
            incompleteFields.push('Education section is required');
          } else {
            // Check if at least one complete entry exists
            const hasCompleteEntry = anquetData.education.some(edu => {
              return section.fields.every(field => {
                if (!field.required) return true;
                const value = edu[field.name];
                return value && String(value).trim();
              });
            });
            if (!hasCompleteEntry) {
              incompleteFields.push('Education: At least one complete entry is required');
            }
          }
        }

        // Check required groups in multi_list_section
        if (section.type === 'multi_list_section' && section.groups) {
          for (const group of section.groups) {
            // Skip if group is not required
            if (group.required !== true) continue;

            // For multi_select groups (soft_skills, technical_skills)
            if (group.type === 'multi_select') {
              const skills = anquetData?.[group.name]?.skills;
              if (!skills || skills.length === 0) {
                incompleteFields.push(`${group.title} section is required`);
              }
            }

            // For list groups (languages, qualifications, awards, social_links)
            if (group.type === 'list') {
              const getListData = () => {
                if (group.name === 'social_links') return anquetData?.personal?.social_links;
                return anquetData?.[group.name];
              };
              const listData = getListData();

              if (!listData || listData.length === 0) {
                incompleteFields.push(`${group.title} section is required`);
              } else {
                // Check if at least one complete entry exists
                const hasCompleteEntry = listData.some(item => {
                  return group.fields.every(field => {
                    if (!field.required) return true;
                    const value = item[field.name];
                    return value && String(value).trim();
                  });
                });
                if (!hasCompleteEntry) {
                  incompleteFields.push(`${group.title}: At least one complete entry is required`);
                }
              }
            }
          }
        }
      }
    }

    // Helper to check list items and collect incomplete field labels
    const checkListSection = (sectionName, sectionLabel, listData) => {
      if (listData && listData.length > 0) {
        const fieldDefs = getFieldDefinitions(sectionName);
        if (fieldDefs) {
          listData.forEach((item, index) => {
            fieldDefs.forEach(fieldDef => {
              if (fieldDef.required) {
                const value = item[fieldDef.name];
                if (!isFieldFilled(value, fieldDef.type)) {
                  incompleteFields.push(`${sectionLabel} #${index + 1}: ${fieldDef.label}`);
                }
              }
            });
          });
        }
      }
    };

    // Check all list sections for incomplete existing entries
    checkListSection('experience', 'Experience', anquetData.experience);
    checkListSection('education', 'Education', anquetData.education);
    checkListSection('languages', 'Language', anquetData.languages);
    checkListSection('qualifications', 'Qualification', anquetData.qualifications);
    checkListSection('awards', 'Award', anquetData.awards);
    checkListSection('social_links', 'Social Link', anquetData.personal?.social_links);

    if (incompleteFields.length === 0) {
      return '';
    }

    return `Please fill in all required fields:\n${incompleteFields.join('\n')}`;
  };

  const areAllRequiredFieldsFilled = () => {
    // Check if the minimum required fields are filled for CV generation
    if (!anquetData) return false;

    // Check required personal information fields from walkthrough-questions.json
    // Only first_name and last_name are marked as required: true
    const firstName = anquetData.personal?.first_name;
    const lastName = anquetData.personal?.last_name;

    const firstNameValid = firstName && String(firstName).trim() !== '';
    const lastNameValid = lastName && String(lastName).trim() !== '';

    // Both first_name and last_name are always required
    if (!firstNameValid || !lastNameValid) return false;

    // Check required sections from walkthrough-questions.json
    if (guideData?.sections) {
      for (const section of guideData.sections) {
        // Check required list sections (experience_list, education_list)
        if (section.type === 'experience_list' && section.required === true) {
          if (!anquetData.experience || anquetData.experience.length === 0) {
            return false;
          }
          // At least one experience must have all required fields filled
          const hasCompleteEntry = anquetData.experience.some(exp => {
            return section.fields.every(field => {
              if (!field.required) return true;
              const value = exp[field.name];
              return value && String(value).trim();
            });
          });
          if (!hasCompleteEntry) return false;
        }

        if (section.type === 'education_list' && section.required === true) {
          if (!anquetData.education || anquetData.education.length === 0) {
            return false;
          }
          // At least one education must have all required fields filled
          const hasCompleteEntry = anquetData.education.some(edu => {
            return section.fields.every(field => {
              if (!field.required) return true;
              const value = edu[field.name];
              return value && String(value).trim();
            });
          });
          if (!hasCompleteEntry) return false;
        }

        // Check required groups in multi_list_section
        if (section.type === 'multi_list_section' && section.groups) {
          for (const group of section.groups) {
            // Skip if group is not required
            if (group.required !== true) continue;

            // For multi_select groups (soft_skills, technical_skills)
            if (group.type === 'multi_select') {
              const skills = anquetData?.[group.name]?.skills;
              if (!skills || skills.length === 0) {
                return false;
              }
            }

            // For list groups (languages, qualifications, awards, social_links)
            if (group.type === 'list') {
              const getListData = () => {
                if (group.name === 'social_links') return anquetData?.personal?.social_links;
                return anquetData?.[group.name];
              };
              const listData = getListData();

              // If list is required, must have at least one complete entry
              if (!listData || listData.length === 0) {
                return false;
              }

              // At least one item must have all required fields filled
              const hasCompleteEntry = listData.some(item => {
                return group.fields.every(field => {
                  if (!field.required) return true;
                  const value = item[field.name];
                  return value && String(value).trim();
                });
              });
              if (!hasCompleteEntry) return false;
            }
          }
        }
      }
    }

    // Now check nested required fields in existing optional sections
    // If a user has started filling a section (it exists and has data),
    // all required nested fields must be filled

    // Check experience list (for optional sections with entries)
    if (anquetData.experience && anquetData.experience.length > 0) {
      const experienceFields = getFieldDefinitions('experience');
      for (const exp of anquetData.experience) {
        if (!validateListItem(exp, experienceFields)) {
          return false;
        }
      }
    }

    // Check education list (for optional sections with entries)
    if (anquetData.education && anquetData.education.length > 0) {
      const educationFields = getFieldDefinitions('education');
      for (const edu of anquetData.education) {
        if (!validateListItem(edu, educationFields)) {
          return false;
        }
      }
    }

    // Check languages list (for existing entries)
    if (anquetData.languages && anquetData.languages.length > 0) {
      const languagesFields = getFieldDefinitions('languages');
      for (const lang of anquetData.languages) {
        if (!validateListItem(lang, languagesFields)) {
          return false;
        }
      }
    }

    // Check qualifications list (for existing entries)
    if (anquetData.qualifications && anquetData.qualifications.length > 0) {
      const qualificationsFields = getFieldDefinitions('qualifications');
      for (const qual of anquetData.qualifications) {
        if (!validateListItem(qual, qualificationsFields)) {
          return false;
        }
      }
    }

    // Check awards list (for existing entries)
    if (anquetData.awards && anquetData.awards.length > 0) {
      const awardsFields = getFieldDefinitions('awards');
      for (const award of anquetData.awards) {
        if (!validateListItem(award, awardsFields)) {
          return false;
        }
      }
    }

    // Check social_links list (for existing entries in personal section)
    if (anquetData.personal?.social_links && anquetData.personal.social_links.length > 0) {
      const socialLinksFields = getFieldDefinitions('social_links');
      for (const link of anquetData.personal.social_links) {
        if (!validateListItem(link, socialLinksFields)) {
          return false;
        }
      }
    }

    return true;
  };

  const isNextButtonDisabled = () => {
    // For the last frame (Generate CV button), use same validation as Preview tab
    // This ensures Generate CV button and Preview tab have consistent enabled/disabled state
    if (guideCurrentFrame === getTotalFrames()) {
      return !areAllRequiredFieldsFilled();
    }

    // For other frames, check if we can proceed to next section
    if (guideCurrentFrame > getTotalFrames()) return true;
    return !isCurrentSectionValid();
  };

  // Simplified keyboard navigation for Guide tab
  const handleKeyDown = (e) => {
    // Remove keyboard shortcuts - Guide tab uses manual navigation only
    // This function kept for compatibility with GuideContent component
  };

  const handleWalkthroughFieldChange = (fieldName, value) => {
    if (fieldName === 'about' || fieldName === 'initial_cv_input') {
      // Top-level fields: 'about' (Frame 5) and 'initial_cv_input' (Frame 1)
      setAnquetData(prev => ({
        ...(prev || {}),
        [fieldName]: value
      }));
    } else {
      // Personal fields
      setAnquetData(prev => ({
        ...(prev || {}),
        personal: {
          ...(prev?.personal || {}),
          [fieldName]: value
        }
      }));
    }
    setHasUnsavedChanges(true);
  };

  

  // Experience list management functions
  const handleWalkthroughExperienceFieldChange = (experienceIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      experience: ((prev?.experience) || []).map((exp, index) =>
        index === experienceIndex
          ? { ...exp, [fieldName]: value }
          : exp
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughExperience = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      experience: [...((prev?.experience) || []), { company: '', role: '', timeframe: '', start_date: '', end_date: '', is_current: false, country: '', city: '', description: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughExperience = (experienceIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      experience: ((prev?.experience) || []).filter((_, index) => index !== experienceIndex)
    }));
    setHasUnsavedChanges(true);
  };

  // Education list management functions
  const handleWalkthroughEducationFieldChange = (educationIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      education: ((prev?.education) || []).map((edu, index) =>
        index === educationIndex
          ? { ...edu, [fieldName]: value }
          : edu
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughEducation = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      education: [...((prev?.education) || []), { level: '', institution: '', timeframe: '', start_date: '', end_date: '', is_current: false, country: '', city: '', items: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughEducation = (educationIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      education: ((prev?.education) || []).filter((_, index) => index !== educationIndex)
    }));
    setHasUnsavedChanges(true);
  };

  // Languages list management functions (Walkthrough)
  const handleWalkthroughLanguageFieldChange = (languageIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      languages: ((prev?.languages) || []).map((lang, index) =>
        index === languageIndex
          ? { ...lang, [fieldName]: value }
          : lang
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughLanguage = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      languages: [...((prev?.languages) || []), { language: '', proficiency: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughLanguage = (languageIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      languages: ((prev?.languages) || []).filter((_, index) => index !== languageIndex)
    }));
    setHasUnsavedChanges(true);
  };

  // Qualifications list management functions (Walkthrough)
  const handleWalkthroughQualificationFieldChange = (qualificationIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      qualifications: ((prev?.qualifications) || []).map((qual, index) =>
        index === qualificationIndex
          ? { ...qual, [fieldName]: value }
          : qual
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughQualification = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      qualifications: [...((prev?.qualifications) || []), { qualification: '', issue_date: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughQualification = (qualificationIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      qualifications: ((prev?.qualifications) || []).filter((_, index) => index !== qualificationIndex)
    }));
    setHasUnsavedChanges(true);
  };

  // Awards list management functions (Walkthrough)
  const handleWalkthroughAwardFieldChange = (awardIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      awards: ((prev?.awards) || []).map((award, index) =>
        index === awardIndex
          ? { ...award, [fieldName]: value }
          : award
      )
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughAward = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      awards: [...((prev?.awards) || []), { award: '', issue_date: '' }]
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughAward = (awardIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      awards: ((prev?.awards) || []).filter((_, index) => index !== awardIndex)
    }));
    setHasUnsavedChanges(true);
  };

  // Social Links list management functions (Walkthrough)
  const handleWalkthroughSocialLinkFieldChange = (linkIndex, fieldName, value) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      personal: {
        ...(prev?.personal || {}),
        social_links: ((prev?.personal?.social_links) || []).map((link, index) =>
          index === linkIndex
            ? { ...link, [fieldName]: value }
            : link
        )
      }
    }));
    setHasUnsavedChanges(true);
  };

  const addWalkthroughSocialLink = () => {
    setAnquetData(prev => ({
      ...(prev || {}),
      personal: {
        ...(prev?.personal || {}),
        social_links: [...((prev?.personal?.social_links) || []), { platform: '', url: '' }]
      }
    }));
    setHasUnsavedChanges(true);
  };

  const removeWalkthroughSocialLink = (linkIndex) => {
    setAnquetData(prev => ({
      ...(prev || {}),
      personal: {
        ...(prev?.personal || {}),
        social_links: ((prev?.personal?.social_links) || []).filter((_, index) => index !== linkIndex)
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Simple frame navigation for Guide tab
  const handleWalkthroughFrameChange = (newFrame) => {
    setGuideCurrentFrame(newFrame);
  };

  // Handle "Next" button click in Guide tab
  const handleGuideNextClick = async () => {
    // If on final frame, check for unsaved changes before generating
    if (guideCurrentFrame === getTotalFrames()) {
      // If no userId exists yet, initialize the CV first
      if (!userId) {
        console.log("🚀 No userId - initializing CV from Generate CV button click");
        await handleInitializeCV();
        return;
      }

      // If there are unsaved changes, save them first
      if (hasUnsavedChanges && anquetData) {
        console.log("💾 Auto-saving changes before generating CV");
        const saveResult = await saveAnquetData(anquetData, true); // Force PDF refresh

        if (!saveResult.success) {
          alert("Failed to save changes before generating CV. Please try again.");
          return;
        }
      } else {
        console.log("📄 No unsaved changes - switching to preview");
      }

      // Switch to preview tab
      setActiveTab('pdf');
      return;
    }

    // First frame: Apply CV if uploaded, then move to next frame
    if (guideCurrentFrame === 1) {
      const initialCvText = (anquetData?.initial_cv_input || '').trim();
      const hasSource = !!initialCvText || !!uploadedFile;
      if (hasSource) {
        const success = await applyUploadedCVToWalkthrough();
        // Only clear sources if the operation succeeded
        if (success) {
          setUploadedFile(null);
          setAnquetData(prev => ({ ...prev, initial_cv_input: '' }));
        } else {
          // If failed, don't proceed to next frame
          return;
        }
      }
      // Move to next frame
      handleWalkthroughFrameChange(guideCurrentFrame + 1);
      return;
    }

    // Default: go to next frame
    handleWalkthroughFrameChange(guideCurrentFrame + 1);
  };

  // Initialize CV from Guide tab data (used by Preview button for new users)
  const handleInitializeCV = async () => {
    setFormSaving(true); // Show loading overlay during CV generation
    // If we're on PDF tab, hide outdated PDF since we're about to create a new one
    if (activeTab === 'pdf') {
      setIsPdfUpdating(true);
    }

    try {
      // Generate simple markdown from anquetData
      const personal = anquetData?.personal || {};
      const experience = anquetData?.experience || [];
      const education = anquetData?.education || [];
      const languages = anquetData?.languages || [];
      const technicalSkills = anquetData?.technical_skills?.skills || [];
      const softSkills = anquetData?.soft_skills?.skills || [];
      const socialLinks = personal?.social_links || [];
      const qualifications = anquetData?.qualifications || [];
      const awards = anquetData?.awards || [];
      const about = anquetData?.about || '';

      // Build markdown text
      let markdownText = '';

      // Personal Information
      if (personal.first_name || personal.last_name) {
        markdownText += `# ${personal.first_name || ''} ${personal.last_name || ''}`.trim() + '\n\n';
      }

      if (personal.email || personal.phone) {
        markdownText += `**Contact:** `;
        if (personal.email) markdownText += personal.email;
        if (personal.email && personal.phone) markdownText += ' | ';
        if (personal.phone) markdownText += personal.phone;
        markdownText += '\n\n';
      }

      if (personal.city || personal.country) {
        markdownText += `**Location:** ${personal.city || ''}${personal.city && personal.country ? ', ' : ''}${personal.country || ''}\n\n`;
      }

      if (personal.job_categories && personal.job_categories.length > 0) {
        markdownText += `**Job Category:** ${personal.job_categories.join(', ')}\n\n`;
      }

      // About
      if (about) {
        markdownText += `## About\n\n${about}\n\n`;
      }

      // Experience
      if (experience.length > 0) {
        markdownText += `## Work Experience\n\n`;
        experience.forEach(exp => {
          if (exp.title || exp.company) {
            markdownText += `### ${exp.title || 'Position'} at ${exp.company || 'Company'}\n`;
          }
          if (exp.timeframe) {
            markdownText += `*${exp.timeframe}*\n\n`;
          }
          if (exp.description) {
            markdownText += `${exp.description}\n\n`;
          }
        });
      }

      // Education
      if (education.length > 0) {
        markdownText += `## Education\n\n`;
        education.forEach(edu => {
          if (edu.degree || edu.institution) {
            markdownText += `### ${edu.degree || 'Degree'} at ${edu.institution || 'Institution'}\n`;
          }
          if (edu.timeframe) {
            markdownText += `*${edu.timeframe}*\n\n`;
          }
          if (edu.description) {
            markdownText += `${edu.description}\n\n`;
          }
        });
      }

      // Technical Skills
      if (technicalSkills.length > 0) {
        markdownText += `## Technical Skills\n\n`;
        markdownText += technicalSkills.join(', ') + '\n\n';
      }

      // Soft Skills
      if (softSkills.length > 0) {
        markdownText += `## Soft Skills\n\n`;
        markdownText += softSkills.join(', ') + '\n\n';
      }

      // Languages
      if (languages.length > 0) {
        markdownText += `## Languages\n\n`;
        languages.forEach(lang => {
          if (lang.language) {
            markdownText += `- ${lang.language}${lang.proficiency ? ` (${lang.proficiency})` : ''}\n`;
          }
        });
        markdownText += '\n';
      }

      // Qualifications
      if (qualifications.length > 0) {
        markdownText += `## Qualifications\n\n`;
        qualifications.forEach(qual => {
          if (qual.title) {
            markdownText += `### ${qual.title}\n`;
          }
          if (qual.issuer) {
            markdownText += `*${qual.issuer}*\n`;
          }
          if (qual.date) {
            markdownText += `${qual.date}\n`;
          }
          if (qual.description) {
            markdownText += `\n${qual.description}\n`;
          }
          markdownText += '\n';
        });
      }

      // Awards
      if (awards.length > 0) {
        markdownText += `## Awards\n\n`;
        awards.forEach(award => {
          if (award.title) {
            markdownText += `### ${award.title}\n`;
          }
          if (award.issuer) {
            markdownText += `*${award.issuer}*\n`;
          }
          if (award.date) {
            markdownText += `${award.date}\n`;
          }
          if (award.description) {
            markdownText += `\n${award.description}\n`;
          }
          markdownText += '\n';
        });
      }

      // Social Links
      if (socialLinks.length > 0) {
        markdownText += `## Links\n\n`;
        socialLinks.forEach(link => {
          if (link.url) {
            markdownText += `- ${link.platform || 'Link'}: ${link.url}\n`;
          }
        });
        markdownText += '\n';
      }

      console.log("🚀 FRONTEND: Generating CV from Guide tab data");
      console.log("🔍 DEBUG: personal.job_categories =", personal.job_categories);
      console.log("🔍 DEBUG: full anquetData =", anquetData);
      console.log("📝 Generated markdown:", markdownText);
      console.log("📏 Markdown length:", markdownText.length);

      let newUserId = userId;
      if (!newUserId) {
        console.log("🆕 Creating new user session via API...");
        const createRes = await fetch(`${BACKEND_ADDRESS}/api/new-user`, { method: 'POST' });
        const createData = await createRes.json();
        if (!createRes.ok || !createData.user_id) {
          throw new Error(createData.message || 'Failed to create new user');
        }
        newUserId = createData.user_id;
        console.log("✅ New user created:", newUserId);
      }

      console.log("🔗 Backend URL:", `${BACKEND_ADDRESS}/api/${newUserId}/initialize-cv`);

      // Backend expects FormData (text_input as Form field), not JSON
      const formData = new FormData();
      formData.append('text_input', markdownText);

      const response = await fetch(`${BACKEND_ADDRESS}/api/${newUserId}/initialize-cv`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      console.log("🎉 CV creation response:", data);

      if (response.ok) {
        // IMPORTANT: Update state BEFORE navigation so questions/score/comment are set even for new sessions
        if (data.suggested_questions && data.suggested_questions.length > 0) {
          setSuggestedQuestions(data.suggested_questions);
          setCurrentQuestion(data.suggested_questions[0]);
          console.log("✅ Updated suggested questions:", data.suggested_questions.slice(0, 3));
        }
        if (data.cv_score !== undefined) {
          setCvScore(data.cv_score);
          console.log("✅ Updated CV score:", data.cv_score);
        }
        if (data.cv_comment) {
          setCvComment(data.cv_comment);
          console.log("✅ Updated CV comment");
        }

        // Navigate to new session if needed (after state updates)
        if (!userId) {
          console.log("🔀 Navigating to new session:", `/${newUserId}`);
          navigate(`/${newUserId}`, { state: { switchToPdf: true } });
          return;
        }

        // If staying on same session, refresh PDF and data
        if (data.pdf_updated) {
          await refreshPDF();
          await fetchAnquetData(true);
          // Clear unsaved changes flag after data is fully loaded
          setHasUnsavedChanges(false);

          // Only switch to Preview tab if all required fields are filled
          if (areAllRequiredFieldsFilled()) {
            setActiveTab('pdf');
          }
        }
      } else {
        alert(data.message || "Failed to create CV");
      }
    } catch (error) {
      console.error("❌ Error creating CV:", error);
      alert("Error creating CV. Please try again.");
      // If there was an error, clear the updating state
      setIsPdfUpdating(false);
    } finally {
      setFormSaving(false); // Clear loading overlay
    }
  };


  // Improve About field in Guide tab using LLM
  const improveWalkthroughAboutWithAI = async () => {
    const currentText = anquetData?.about || '';
    if (!currentText.trim()) return;

    // Require userId for Guide tab AI improvements
    if (!userId) {
      alert('Please initialize your CV first before using AI improvements.');
      return;
    }

    try {
      setIsImprovingField(true);
      setImprovementTarget({ type: 'about', index: null });
      const targetUserId = userId;

      const payload = { field_name: 'about', current_text: currentText };
      const res = await fetch(`${BACKEND_ADDRESS}/api/${targetUserId}/improve-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success') {
        alert(data.message || 'Failed to improve text');
        return;
      }
      setAnquetData(prev => ({ ...prev, about: data.improved_text || '' }));
      setHasUnsavedChanges(true);
    } catch (e) {
      console.error('❌ Error improving walkthrough about:', e);
      alert('Error improving text. Please try again.');
    } finally {
      setIsImprovingField(false);
      setImprovementTarget(null);
    }
  };

  // Apply uploaded CV and/or initial CV text to prefill Guide tab fields using backend LLM
  const applyUploadedCVToWalkthrough = async () => {
    try {
      const initialCvText = (anquetData?.initial_cv_input || '').trim();
      if (!uploadedFile && !initialCvText) {
        alert('Please provide initial CV text or upload a CV file first.');
        return false;
      }
      setIsPrefillingFromCV(true);

      // Create userId if it doesn't exist (like handleInitializeCV does)
      let targetUserId = userId;
      if (!targetUserId) {
        console.log("🆕 Creating new user session via API...");
        const createRes = await fetch(`${BACKEND_ADDRESS}/api/new-user`, { method: 'POST' });
        const createData = await createRes.json();
        if (!createRes.ok || !createData.user_id) {
          throw new Error(createData.message || 'Failed to create new user');
        }
        targetUserId = createData.user_id;
        console.log("✅ New user created:", targetUserId);
      }

      // Call initialize-cv with whichever inputs are available (file and/or text)
      let initRes;
      let initData;
      if (uploadedFile) {
        const formData = new FormData();
        if (initialCvText) formData.append('text_input', initialCvText);
        formData.append('document', uploadedFile);
        initRes = await fetch(`${BACKEND_ADDRESS}/api/${targetUserId}/initialize-cv`, {
          method: 'POST',
          body: formData
        });
        initData = await initRes.json();
      } else {
        // Text only. The backend reads text_input as a multipart Form field, so it must
        // be sent as FormData — a JSON body is silently ignored and yields an empty CV.
        const formData = new FormData();
        formData.append('text_input', initialCvText);
        initRes = await fetch(`${BACKEND_ADDRESS}/api/${targetUserId}/initialize-cv`, {
          method: 'POST',
          body: formData
        });
        initData = await initRes.json();
      }
      if (!initRes.ok || initData.status !== 'success') {
        throw new Error(initData.message || 'Failed to parse uploaded CV');
      }

      // Prefer cv_data from initialize-cv, fallback to fetching anquet endpoint
      let cvData = initData.cv_data;
      if (!cvData) {
        const anquetRes = await fetch(`${BACKEND_ADDRESS}/${targetUserId}/anquet`);
        const anquetJson = await anquetRes.json();
        if (!anquetRes.ok || anquetJson.status !== 'success') {
          throw new Error(anquetJson.message || 'Failed to load parsed data');
        }
        cvData = anquetJson.anquet;
      }

      // Map cvData -> anquetData
      const personal = cvData.personal || {};

      // Get job_categories as array from backend data
      const jobCategories = Array.isArray(personal.job_categories) ? personal.job_categories : [];

      setAnquetData(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          first_name: personal.first_name || '',
          last_name: personal.last_name || '',
          email: personal.email || '',
          phone: personal.phone || '',
          country: personal.country || '',
          city: personal.city || '',
          job_categories: jobCategories.length > 0 ? jobCategories : prev.personal?.job_categories || []
        },
        // Use backend's generated about field from LLM, fallback to existing value
        about: cvData.about || anquetData?.about || '',
        // Update skills
        technical_skills: {
          skills: (cvData.technical_skills && Array.isArray(cvData.technical_skills.skills))
            ? cvData.technical_skills.skills
            : []
        },
        soft_skills: {
          skills: (cvData.soft_skills && Array.isArray(cvData.soft_skills.skills))
            ? cvData.soft_skills.skills
            : []
        }
      }));

      // Experiences
      const mapExperience = (exp) => {
        let desc = '';
        if (Array.isArray(exp.description)) desc = exp.description.join('\n');
        else if (typeof exp.description === 'string') desc = exp.description;
        
        // Parse existing timeframe if start_date/end_date are missing
        let startDate = exp.start_date || '';
        let endDate = exp.end_date || '';
        if ((!startDate || !endDate) && exp.timeframe) {
          const parsed = parseTimeframe(exp.timeframe);
          startDate = startDate || parsed.start;
          endDate = endDate || (parsed.present ? 'Present' : parsed.end);
        }
        
        return {
          company: exp.company || '',
          role: exp.role || '',
          timeframe: exp.timeframe || '',
          start_date: startDate,
          end_date: endDate,
          country: exp.country || '',
          city: exp.city || '',
          description: desc || '',
          is_current: exp.is_current === true  // Ensure boolean
        };
      };
      const experiences = Array.isArray(cvData.experience) ? cvData.experience.map(mapExperience) : [];
      // Don't add empty items - let users add them explicitly if needed

      // Education
      const mapEducation = (edu) => {
        let itemsTxt = '';
        if (Array.isArray(edu.items)) itemsTxt = edu.items.join('\n');
        else if (typeof edu.items === 'string') itemsTxt = edu.items;

        // Parse existing timeframe if start_date/end_date are missing
        let startDate = edu.start_date || '';
        let endDate = edu.end_date || '';
        if ((!startDate || !endDate) && edu.timeframe) {
          const parsed = parseTimeframe(edu.timeframe);
          startDate = startDate || parsed.start;
          endDate = endDate || (parsed.present ? 'Present' : parsed.end);
        }

        return {
          level: edu.level || '',
          institution: edu.institution || '',
          country: edu.country || '',
          city: edu.city || '',
          items: itemsTxt || '',
          timeframe: edu.timeframe || '',
          start_date: startDate,
          end_date: endDate,
          is_current: edu.is_current || false
        };
      };
      const education = Array.isArray(cvData.education) ? cvData.education.map(mapEducation) : [];
      // Don't add empty items - let users add them explicitly if needed

      // Languages
      const languages = Array.isArray(cvData.languages)
        ? cvData.languages.map(l => ({ language: l.language || '', proficiency: l.proficiency || '' }))
        : [];

      // Social Links
      const socialLinks = Array.isArray(personal.social_links)
        ? personal.social_links.map(sl => ({ platform: sl.platform || '', url: sl.url || '' }))
        : [];

      // Qualifications
      const qualifications = Array.isArray(cvData.qualifications)
        ? cvData.qualifications.map(q => ({ qualification: q.qualification || '', issue_date: q.issue_date || '' }))
        : [];

      // Awards
      const awards = Array.isArray(cvData.awards)
        ? cvData.awards.map(a => ({ award: a.award || '', issue_date: a.issue_date || '' }))
        : [];

      // Update anquetData with all the parsed data
      setAnquetData(prev => ({
        ...prev,
        experience: experiences,
        education: education,
        languages: languages,
        qualifications: qualifications,
        awards: awards,
        personal: {
          ...prev.personal,
          social_links: socialLinks
        }
      }));

      setHasUnsavedChanges(true);

      // If we only had About text and no structured fields came back, try enriching via process-input
      const noExp = !Array.isArray(cvData.experience) || cvData.experience.length === 0;
      const noEdu = !Array.isArray(cvData.education) || cvData.education.length === 0;
      const noTech = !((cvData.technical_skills && Array.isArray(cvData.technical_skills.skills)) && cvData.technical_skills.skills.length > 0);
      const noSoft = !((cvData.soft_skills && Array.isArray(cvData.soft_skills.skills)) && cvData.soft_skills.skills.length > 0);

      if (initialCvText && noExp && noEdu && noTech && noSoft) {
      try {
          // Build an enrichment prompt using available taxonomy for better grounding
          const jobCats = (taxonomyData.jobCategories || []).slice(0, 50).join(', ');
          const techList = (taxonomyData.technicalSkills || []).slice(0, 100).join(', ');
          const softList = (taxonomyData.softSkills || []).slice(0, 100).join(', ');
          const langList = (taxonomyData.languages || []).slice(0, 50).join(', ');
          const proficiencyList = (taxonomyData.proficiencyLevels || []).join(', ');
          const citiesList = (taxonomyData.cities || []).slice(0, 50).join(', ');
          const countriesList = (taxonomyData.countries || []).slice(0, 50).join(', ');
          const educationLevelsList = (taxonomyData.educationLevels || []).join(', ');
          const socialPlatformsList = (taxonomyData.socialPlatforms || []).join(', ');

          // Check if personal fields are empty - if so, include them in enrichment
          const personalFieldsEmpty = !personal.first_name && !personal.last_name && !personal.city;
          const personalFieldsPrompt = personalFieldsEmpty
            ? `- personal.first_name, personal.last_name: extract the person's name if mentioned.\n` +
              `- personal.city, personal.country: extract location if mentioned.\n`
            : '';

          // Check if contact fields are empty
          const noPhone = !personal.phone;
          const noEmail = !personal.email;
          const contactPrompt = (noPhone || noEmail)
            ? `${noPhone ? '- personal.phone: extract phone number if mentioned.\n' : ''}` +
              `${noEmail ? '- personal.email: extract email address if mentioned.\n' : ''}`
            : '';

          // Check if languages are empty
          const noLanguages = !Array.isArray(cvData.languages) || cvData.languages.length === 0;
          const languagesPrompt = noLanguages
            ? `- languages: extract languages mentioned with proficiency levels. Use language names from [${langList}] and proficiency levels from [${proficiencyList}].\n`
            : '';

          // Check if experience is empty - if so, include extraction instructions
          const experiencePrompt = noExp
            ? `- experience: extract work history entries. For each job include: role (job title), company, city (prefer from [${citiesList}] or use "Other"), country (from [${countriesList}]), timeframe (YYYY-MM -- YYYY-MM or YYYY-MM -- Present for current jobs), description (2-4 achievement bullets).\n`
            : '';

          // Check if education is empty - if so, include extraction instructions
          const educationPrompt = noEdu
            ? `- education: extract education entries. For each include: institution name, level (from [${educationLevelsList}]), city (prefer from [${citiesList}] or use "Other"), country (from [${countriesList}]), timeframe (YYYY-MM -- YYYY-MM), items (relevant coursework, activities, or field of study).\n`
            : '';

          // Check if social links are empty
          const noSocialLinks = !Array.isArray(personal.social_links) || personal.social_links.length === 0;
          const socialLinksPrompt = noSocialLinks
            ? `- personal.social_links: extract social media profile URLs. For each include: platform (MUST be one of [${socialPlatformsList}]), url. Only include platforms from the list.\n`
            : '';

          // Check if qualifications are empty
          const noQualifications = !Array.isArray(cvData.qualifications) || cvData.qualifications.length === 0;
          const qualificationsPrompt = noQualifications
            ? `- qualifications: extract certifications and licenses from "Licenses & Certifications" section. For each include: qualification (the certification title/name), issue_date (YYYY-MM format from "Issued" date).\n`
            : '';

          // Check if awards are empty
          const noAwards = !Array.isArray(cvData.awards) || cvData.awards.length === 0;
          const awardsPrompt = noAwards
            ? `- awards: extract honors and awards from "Honors & Awards" section. For each include: award (the award title/name), issue_date (YYYY-MM format from "Issued" date).\n`
            : '';

          // Check if about is empty
          const noAbout = !cvData.about || cvData.about.trim() === '';
          const aboutPrompt = noAbout
            ? `- about: extract or summarize the professional summary ONLY if one exists in the text, or if there is sufficient experience/skills data to base it on. Leave as null if insufficient information.\n`
            : '';

          const enrichPrompt = `From the following professional summary, infer and populate the CV fields in JSON: \n\n` +
            personalFieldsPrompt +
            contactPrompt +
            experiencePrompt +
            educationPrompt +
            socialLinksPrompt +
            qualificationsPrompt +
            awardsPrompt +
            aboutPrompt +
            `- personal.job_categories: choose 1-3 most relevant categories from this list [${jobCats}] as an array (or leave empty array if no good match).\n` +
            `- technical_skills.skills: extract technical skills mentioned or clearly implied in the text (programming languages, frameworks, tools, technologies). Map to values from [${techList}]. Leave as empty array if no technical skills can be reasonably inferred.\n` +
            `- soft_skills.skills: extract soft skills mentioned or clearly demonstrated in the text. Map to values from [${softList}]. Leave as empty array if no soft skills can be reasonably inferred.\n` +
            languagesPrompt +
            `Do not remove any existing content. Only add to the specified fields.\n\n` +
            `Summary: """${initialCvText}"""`;

          // Ensure session exists (already ensured above via targetUserId)
          const piRes = await fetch(`${BACKEND_ADDRESS}/api/${targetUserId}/process-input`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userInput: enrichPrompt, currentQuestion: 'Enrich skills and category' })
          });
          const piData = await piRes.json();

          if (piRes.ok && piData.status === 'success') {
            // Reload latest anquet and map again
            const anquetRes2 = await fetch(`${BACKEND_ADDRESS}/${targetUserId}/anquet`);
            const anquetJson2 = await anquetRes2.json();
            if (anquetRes2.ok && anquetJson2.status === 'success') {
              const newCv = anquetJson2.anquet || {};
              const np = newCv.personal || {};
              const nTech = (newCv.technical_skills && Array.isArray(newCv.technical_skills.skills)) ? newCv.technical_skills.skills : [];
              const nSoft = (newCv.soft_skills && Array.isArray(newCv.soft_skills.skills)) ? newCv.soft_skills.skills : [];
              const nLang = Array.isArray(newCv.languages) ? newCv.languages : [];
              const nExp = Array.isArray(newCv.experience) ? newCv.experience : [];
              const nEdu = Array.isArray(newCv.education) ? newCv.education : [];
              const nQualifications = Array.isArray(newCv.qualifications) ? newCv.qualifications : [];
              const nAwards = Array.isArray(newCv.awards) ? newCv.awards : [];
              const nSocialLinks = Array.isArray(np.social_links) ? np.social_links : [];
              const nAbout = newCv.about || '';

              // Get job_categories as array
              const nJobCats = Array.isArray(np.job_categories) ? np.job_categories : [];

              setAnquetData(prev => ({
                ...prev,
                personal: {
                  ...prev.personal,
                  // Include personal fields from enrichment if they were extracted
                  first_name: np.first_name || prev.personal?.first_name || '',
                  last_name: np.last_name || prev.personal?.last_name || '',
                  city: np.city || prev.personal?.city || '',
                  country: np.country || prev.personal?.country || '',
                  phone: np.phone || prev.personal?.phone || '',
                  email: np.email || prev.personal?.email || '',
                  social_links: nSocialLinks.length > 0 ? nSocialLinks : prev.personal?.social_links || [],
                  job_categories: nJobCats.length > 0 ? nJobCats : prev.personal?.job_categories || []
                },
                technical_skills: {
                  skills: nTech.length > 0 ? nTech : prev.technical_skills?.skills || []
                },
                soft_skills: {
                  skills: nSoft.length > 0 ? nSoft : prev.soft_skills?.skills || []
                },
                // Include languages from enrichment if they were extracted
                languages: nLang.length > 0 ? nLang : prev.languages || [],
                // Include experience and education from enrichment if they were extracted
                experience: nExp.length > 0 ? nExp : prev.experience || [],
                education: nEdu.length > 0 ? nEdu : prev.education || [],
                // Include qualifications, awards, and about from enrichment
                qualifications: nQualifications.length > 0 ? nQualifications : prev.qualifications || [],
                awards: nAwards.length > 0 ? nAwards : prev.awards || [],
                about: nAbout || prev.about || ''
              }));
            }
          }
        } catch (_) {
          // Enrichment is best-effort; ignore failures
        }
      }

      // If we created a new userId, navigate to it and stay on Guide tab at first form step
      if (!userId && targetUserId) {
        console.log("🔀 Navigating to new session:", `/${targetUserId}`);
        navigate(`/${targetUserId}`, { state: { stayOnGuide: true, guideFrame: 2 } });
      }

      return true; // Success
    } catch (e) {
      console.error('❌ Failed to apply uploaded CV to walkthrough:', e);
      alert(e.message || 'Failed to apply uploaded CV. Please try again.');
      return false; // Failure
    } finally {
      setIsPrefillingFromCV(false);
    }
  };

  // handleWalkthroughGenerateInitial removed (unused)

  // Loading state
  if (pdfLoading) {
    return (
      <div className="app">
        <div className="pdf-section">
          <div className="pdf-header">
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button className="tab-button active">
              Preview
            </button>
            <button className="tab-button">
              Form
            </button>
          </div>

          <div className="pdf-container">
            <div className="pdf-placeholder">
              <RefreshCw size={48} className="spinning" />
              <p>Loading PDF...</p>
            </div>
          </div>
        </div>
        <div className="controls-section">
          <div className="current-question">
            <h4>Please wait...</h4>
            <p>Initializing your CV builder</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (pdfError) {
    return (
      <div className="app">
        <div className="pdf-section">
          <div className="pdf-header">
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button className="tab-button active">
              Preview
            </button>
            <button className="tab-button">
              Switch to form
            </button>
          </div>

          <div className="pdf-container">
            <div className="pdf-placeholder error">
              <FileText size={48} />
              <p>Error: {pdfError}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="retry-button"
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  background: '#4299e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
        <div className="controls-section">
          <div className="current-question">
            <h4>Connection Error</h4>
            <p>Please check if the backend is running</p>
          </div>
        </div>
      </div>
    );
  }

  // Main app
  // Determine if AI assistant should be shown
  const shouldShowAIAssistant = !(guideCurrentFrame === 1 && !pdfUrl);

  return (
    <div className={`app ${shouldShowAIAssistant ? '' : 'ai-hidden'}`}>
      <div className="pdf-section">
        <div className="pdf-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {pdfUrl && (
              <button
                className="button_large_bold py-2 px-4 md:py-3 md:px-6 flex whitespace-nowrap items-center justify-center gap-2 transition duration-200 ease-out text-white hover:brightness-125 border border-solid rounded-full disabled:opacity-30 disabled:cursor-not-allowed bg-brand-orange-600 border-brand-orange-600"
                onClick={handleExportJSON}
                disabled={isProcessing}
                title="Export JSON"
              >
                <span>JSON</span>
              </button>
            )}
            <OnboardingHelpButton windowName="main" />
            {activeTab === 'pdf' && pdfUrl && (
              <button
                className="button_large_bold py-2 px-4 md:py-3 md:px-6 flex whitespace-nowrap items-center justify-center gap-2 transition duration-200 ease-out text-white hover:brightness-125 border border-solid rounded-full disabled:opacity-30 disabled:cursor-not-allowed bg-brand-orange-600 border-brand-orange-600"
                onClick={handleExport}
                disabled={isProcessing}
                title="Download CV as PDF"
              >
                <span>Download CV</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation" data-onboard="tabs">
          <button
            className={`tab-button ${activeTab === 'guide' ? 'active' : ''}`}
            onClick={() => setActiveTab('guide')}
            disabled={formSaving || isProcessing}
            data-onboard="guide-tab"
          >
            Guide
          </button>
          <button
            className={`tab-button ${activeTab === 'pdf' ? 'active' : ''}`}
            onClick={handlePreviewTabClick}
            disabled={formSaving || isProcessing || !areAllRequiredFieldsFilled()}
            title={!areAllRequiredFieldsFilled() ? getIncompleteFieldsMessage() : ''}
            data-onboard="preview-tab"
          >
            Preview
          </button>
        </div>

        {/* Tab Content */}
        <div className="pdf-container">
          {activeTab === 'guide' ? (
            <div className="form-content" style={{ position: 'relative' }}>
              {/* Guide tab content - Reuses walkthrough rendering with anquetData */}
              <div className="px-5 py-5 max-w-[900px] mx-auto">
                <div className="flex flex-col items-center rounded-3xl px-4 py-6 shadow-xs md:px-8 md:py-8 bg-white">
                  <div className="flex w-full max-w-[762px] flex-col">
                    <div className="paragraph_large_bold mb-4 pb-4 border-b border-solid border-gray-200 text-gray-700">
                    {guideCurrentFrame !== 1 && (
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                        Step {guideCurrentFrame - 1}/{getTotalFrames() - 1}
                      </div>
                    )}
                    {getCurrentSection() && (
                      <>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '4px' }}>
                          {getCurrentSection().title}
                        </div>
                        <div style={{ fontSize: '14px', color: '#64748b' }}>
                          {getCurrentSection().description}
                        </div>
                      </>
                    )}
                    </div>

                    {/* Guide content - Uses shared GuideContent component */}
                    <GuideContent
                    walkthroughCurrentFrame={guideCurrentFrame}
                    getCurrentSection={getCurrentSection}
                    getTotalFrames={getTotalFrames}
                    anquetData={anquetData}
                    uploadedFile={uploadedFile}
                    setUploadedFile={setUploadedFile}
                    taxonomyData={taxonomyData}
                    taxonomyLoading={taxonomyLoading}
                    handleWalkthroughFieldChange={handleWalkthroughFieldChange}
                    handleWalkthroughExperienceFieldChange={handleWalkthroughExperienceFieldChange}
                    handleWalkthroughEducationFieldChange={handleWalkthroughEducationFieldChange}
                    handleWalkthroughLanguageFieldChange={handleWalkthroughLanguageFieldChange}
                    handleWalkthroughQualificationFieldChange={handleWalkthroughQualificationFieldChange}
                    handleWalkthroughAwardFieldChange={handleWalkthroughAwardFieldChange}
                    handleWalkthroughSocialLinkFieldChange={handleWalkthroughSocialLinkFieldChange}
                    setAnquetData={setAnquetData}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                    addWalkthroughExperience={addWalkthroughExperience}
                    removeWalkthroughExperience={removeWalkthroughExperience}
                    addWalkthroughEducation={addWalkthroughEducation}
                    removeWalkthroughEducation={removeWalkthroughEducation}
                    addWalkthroughLanguage={addWalkthroughLanguage}
                    removeWalkthroughLanguage={removeWalkthroughLanguage}
                    addWalkthroughQualification={addWalkthroughQualification}
                    removeWalkthroughQualification={removeWalkthroughQualification}
                    addWalkthroughAward={addWalkthroughAward}
                    removeWalkthroughAward={removeWalkthroughAward}
                    addWalkthroughSocialLink={addWalkthroughSocialLink}
                    removeWalkthroughSocialLink={removeWalkthroughSocialLink}
                    dragActive={dragActive}
                    handleDrag={handleDrag}
                    handleDrop={handleDrop}
                    handleFileChange={handleFileChange}
                    showLinkedInHelp={showLinkedInHelp}
                    setShowLinkedInHelp={setShowLinkedInHelp}
                    linkedInIconRef={linkedInIconRef}
                    setLinkedInIconRef={setLinkedInIconRef}
                    hoveredField={hoveredField}
                    setHoveredField={setHoveredField}
                    improveWalkthroughAboutWithAI={improveWalkthroughAboutWithAI}
                    isImprovingField={isImprovingField}
                    improvementTarget={improvementTarget}
                    parseTimeframe={parseTimeframe}
                    combineTimeframe={combineTimeframe}
                    isValidYearMonth={isValidYearMonth}
                    handleKeyDown={handleKeyDown}
                    isProcessing={isProcessing}
                  />

                  <div className="walkthrough-footer" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                    <button
                      className="paragraph_large_bold flex items-center gap-4 text-brand-orange-600 underline-offset-2 hover:underline cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      onClick={() => handleWalkthroughFrameChange(guideCurrentFrame - 1)}
                      disabled={guideCurrentFrame === 1 || isProcessing}
                    >
                      <svg className="h-6 w-6" viewBox="0 0 28 23" fill="none">
                        <path d="M11.3333 20.8337L2 11.5003M2 11.5003L11.3333 2.16699M2 11.5003H26" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="whitespace-nowrap">Previous step</span>
                    </button>
                    <button
                      className="button_large_bold py-2 px-4 md:py-3 md:px-6 flex whitespace-nowrap items-center justify-center gap-2 transition duration-200 ease-out text-white hover:brightness-125 border border-solid rounded-full disabled:opacity-30 disabled:cursor-not-allowed bg-brand-orange-600 border-brand-orange-600"
                      onClick={handleGuideNextClick}
                      disabled={
                        // For first frame, only disable when applying; for others, keep validation and form saving locks
                        (guideCurrentFrame === 1 && (isPrefillingFromCV || isProcessing)) ||
                        (guideCurrentFrame !== 1 && (isNextButtonDisabled() || formSaving || isProcessing))
                      }
                    >
                      {guideCurrentFrame === getTotalFrames() ? (
                        formSaving ? (
                          <>
                            <RefreshCw size={14} className="spinning" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <span>Generate CV</span>
                        )
                      ) : guideCurrentFrame === 1 ? (
                        isPrefillingFromCV ? (
                          <>
                            <RefreshCw size={14} className="spinning" />
                            <span>Applying...</span>
                          </>
                        ) : (
                          <span>{(anquetData?.initial_cv_input && anquetData.initial_cv_input.trim()) || uploadedFile ? 'Generate CV with AI' : 'Input manually'}</span>
                        )
                      ) : (
                        <span>Continue</span>
                      )}
                    </button>
                  </div>
                  </div>
                </div>
              </div>

              {/* Loading Overlay - Show when generating CV or AI is processing */}
              {(formSaving || isPrefillingFromCV || isProcessing) && (
                <div className="guide-saving-overlay">
                  <div className="overlay-content">
                    <RefreshCw size={48} className="spinning overlay-icon" />
                    <p className="overlay-text">
                      {isPrefillingFromCV ? 'Generating CV with AI...' : isProcessing ? 'Processing your request...' : 'Generating CV Preview...'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'pdf' ? (
            isPdfUpdating ? (
              <div className="pdf-placeholder">
                <RefreshCw size={48} className="spinning" />
                <p>Updating PDF...</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                width="100%"
                height="100%"
                className="border-0"
                style={{ minHeight: '600px' }}
                title="CV Preview"
                data-tour="pdf-preview"
              />
            ) : (
              <div className="pdf-placeholder">
                <FileText size={48} />
                <p>No PDF available</p>
              </div>
            )
          ) : null}
        </div>
      </div>

      {shouldShowAIAssistant && (
        <div className="controls-section">
        {/* AI Assistant Tile */}
        <div className="ai-assistant-tile" data-onboard="ai-assistant">
          {/* Prominent Current Question */}
          <div className="current-question-prominent" data-onboard="current-question">
            <p className="text-brand-orange-300 paragraph_large_bold">{currentQuestion}</p>
          </div>

          {/* Enhanced Input Form */}
          <form onSubmit={handleSubmit} className="input-form-enhanced">
            <div className="input-container-enhanced bg-gray-100" data-tour="ai-custom-prompt">
              <Bot size={18} className="ai-input-bot-icon" />
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={currentQuestion === "Custom request" ? "What would you like to update or add about yourself?" : "Type your answer here..."}
                className="question-input-enhanced paragraph_normal_regular"
                disabled={isProcessing}
                data-onboard="input"
              />
              <button
                type="submit"
                className="button_large_bold py-2 px-4 flex items-center justify-center gap-2 whitespace-nowrap transition duration-200 ease-out text-white bg-brand-orange-600 border-brand-orange-600 border border-solid rounded-full hover:brightness-125 disabled:opacity-30 disabled:cursor-not-allowed ai-send-button"
                disabled={isProcessing || !userInput.trim()}
                data-onboard="send-button"
              >
                {isProcessing ? (
                  <RefreshCw size={20} className="spinning" />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Other Suggested Questions */}
        <div className="suggested-questions" data-tour="ai-questions" data-onboard="ai-questions">
          <h5>Other suggested questions:</h5>
          {suggestedQuestions.map((question, index) => (
            <button
              key={index}
              className="ai-suggestion-button"
              onClick={() => handleQuestionClick(question)}
              disabled={isProcessing}
            >
              {question}
            </button>
          ))}
        </div>

        {/* CV Score Display with expandable details */}
        <div className="cv-score-section" data-tour="readiness-score">
          <div 
            className="score-display" 
            style={{
              ...getScoreStyle(cvScore),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            <span className="score-text">Readiness: {cvScore}/10</span>
            {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          
          {showDetails && (
            <div className="score-details">
              <div className="score-label">{getScoreLabel(cvScore)}</div>
              <div className="comment-box">
                <p>{cvComment}</p>
              </div>
            </div>
          )}
        </div>

        {/* Export CV and Export JSON (dev) removed as requested */}

        {/* Create New CV button temporarily removed as requested */}

        {/* Walkthrough button removed as requested */}
        </div>
      )}

    {/* Create New CV Modal */}
    {showCreateNewModal && (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Tell us about yourself to start building your CV</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Help button for old hints removed */}
              <button 
                className="modal-close"
                onClick={() => {
                  setShowCreateNewModal(false);
                  setNewCvText("");
                  setUploadedFile(null);
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          <div className="modal-body">
            <div className="text-input-container">
              {/* Animated placeholder - only show when input is empty */}
              {newCvText.length === 0 && (
                <div className="animated-placeholder">
                  {displayedText}
                  {isTyping && <span className="typing-cursor">|</span>}
                </div>
              )}
              
              {/* LinkedIn Help Icon */}
              <div 
                ref={(ref) => setLinkedInIconRef(ref)}
                className="linkedin-help-icon"
                onMouseEnter={() => setShowLinkedInHelp(true)}
                onMouseLeave={() => setShowLinkedInHelp(false)}
              >
                <LinkedInLogo />
              </div>

              {/* LinkedIn Help Popup */}
              {showLinkedInHelp && linkedInIconRef && (
                <LinkedInHelpPopup iconRef={linkedInIconRef} />
              )}
              
              {/* Textarea - must have transparent background */}
              <textarea
                className="cv-text-input"
                placeholder="" // Remove the static placeholder text
                value={newCvText}
                onChange={(e) => setNewCvText(e.target.value)}
                data-onboard="cv-text-input"
              />
            </div>
            
            <div className="divider-text">... or add your old CV</div>
            
            <div 
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${uploadedFile ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              data-onboard="file-upload"
            >
              <input
                id="file-input"
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
              />
              
              {uploadedFile ? (
                <div className="file-info">
                  <FileText size={24} />
                  <span>{uploadedFile.name}</span>
                  <button 
                    className="remove-file"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadedFile(null);
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="file-drop-content">
                  <Upload size={32} />
                  <p>Drag & drop your resume/CV here</p>
                  <span>or click to browse files</span>
                  <small>Supports PDF, DOC, DOCX, TXT</small>
                </div>
              )}
            </div>
          </div>
          
          <div className="modal-footer">
            <button
              className="btn btn-violet btn-md"
              onClick={handleCreateNewCV}
              disabled={isCreatingCV || (!newCvText.trim() && !uploadedFile)}
              data-onboard="generate-button"
            >
              {isCreatingCV ? (
                <>
                  <RefreshCw size={16} className="spinning" />
                  Generating...
                </>
              ) : (
                'Generate Initial CV'
              )}
            </button>
          </div>
        </div>
        {/* Old onboarding hints disabled */}
      </div>
    )}

    {/* Onboarding Hints */}
    <OnboardingHints windowName="main" activeTab={activeTab} />

    </div>
  );
}

function App() {
  // Use PUBLIC_URL as basename for proper routing when served from subdirectory
  // This is automatically set from the "homepage" field in package.json
  const basename = process.env.PUBLIC_URL || '';

  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route path="/" element={<CVBuilder />} />
        <Route path="/:userId" element={<CVBuilder />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
